"use client";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChatWindow } from "components/ChatWindow";
import { useParams, useOutletContext } from "react-router-dom";
import { fetchWrapper } from "services/api-client/fetchWrapper";
import { IPropertyDataDetails } from "../../types/services";
import { isEmpty, twoArraysToObject } from "utils/helpers";
import PropertyCard from "components/PropertyCard/PropertyCard";
import { captureException } from "scripts/logging/error-report";
import { useQueryFlags } from "scripts/hooks/useQueryFlags";
import { usePreservedRouteKey } from "scripts/hooks/usePreservedRouteKey";
import { ShowNotFoundOrUnAuthorizedUiBasedOnStatusCode } from "components/ShowNotFoundOrUnAuthorizedUiBasedOnStatusCode";
import { IUser } from "services/UserContext";
import { IAssistant, IChatSession } from "layouts/DefaultLayout";
import ErrorBoundary from "components/ErrorBoundary";
import ChatErrorFallback from "components/ChatErrorFallback";
import { useShareContext } from "services/ShareContext";
import { trackEvent, matomoUserId } from "services/api-client/matomoTracker";
import { MetaHead } from "components/MetaHead";
import { GUEST_USER_ALLOWED } from "utils/constants";
import { SidebarSignUp } from "components/SidebarSignUp";

const questions: string[] = [
  "List the assigned schools",
  "Tell me about the neighborhood",
  "Is there a maintenance/HOA fee?",
  "Tell me about the Listing Agent",
];

interface OutletContext {
  user?: IUser;
  setAssistant(assistant: IAssistant.Input): void;
  onNewAssistantSession(date: string, session: IChatSession): void;
  statusCode: number;
  blockedByGuestAccess: boolean;
}

export default function ListingChatbotPage() {
  const { isContentNotVisible, source } = useQueryFlags();
  const { harid, session } = useParams();
  const isListingDetail = source === "listing-detail";
  const chatKey = usePreservedRouteKey();

  const {
    user,
    setAssistant,
    onNewAssistantSession,
    statusCode,
    blockedByGuestAccess
  } = useOutletContext<OutletContext>();

  const { listingDetailShareUrlRef } = useShareContext();

  // Built here because the page renders with it, then handed to the layout from
  // an effect — calling the layout's setter during this render is what triggers
  // React's "Cannot update a component while rendering a different component".
  const assistant = useMemo<IAssistant>(
    () => ({
      id: `property`,
      type: "property",
      name: "property",
      sessionId: session,
    }),
    [session]
  );

  useLayoutEffect(() => {
    setAssistant(assistant);
  }, [assistant, setAssistant]);

  const hasTrackedVisit = useRef(false);
  useEffect(() => {
    if (!user?.user_id || hasTrackedVisit.current) return;
    hasTrackedVisit.current = true;
    trackEvent(`AI Mode / Listing Assistant Visit / ${harid ?? '0'}`, { uid: matomoUserId(user) });
  }, [harid, user?.user_id, user?.username]);
  const [propertyDetails, setPropertyDetails] =
    useState<IPropertyDataDetails>();
  const propertyName = !isEmpty(propertyDetails?.detail.address)
    ? propertyDetails?.detail.address
    : "";

  useEffect(() => {
    listingDetailShareUrlRef.current = null;
    if (!harid) return;

    const controller = new AbortController();
    let isMounted = true;

    (async () => {
      try {
        const { data } = await fetchWrapper.get(
          `/api/v1/chatbot/property-details/${harid}`,
          controller.signal
        );

        const detailItems = twoArraysToObject(
          data.detail.detailitems.titles,
          data.detail.detailitems.values
        );
        listingDetailShareUrlRef.current = data?.share_url ?? null;
        if (isMounted && !!data) {
          setPropertyDetails({ ...data, detailItems });
        }
      } catch (e: any) {
        // Ignore expected aborts from unmount/rerender
        if (e?.name === 'AbortError') return;
        listingDetailShareUrlRef.current = null;
        captureException(e);
        console.error('[property-details] fetch failed:', e);
      }
    })();

    return () => {
      isMounted = false;
      controller.abort();
      listingDetailShareUrlRef.current = null;
    };
  }, [harid, listingDetailShareUrlRef]);

  useEffect(() => {
    if (window.parent && window.parent !== window && session) {
      window.parent.postMessage({
        type: 'SESSION_ID',
        data: { sessionId: session },
      }, '*');
    }
  }, [session]);


  const Template = useCallback(
    (props: ChatWindow.ICustomTemplateProps) => {
      const { isEmpty, Content, Title, Messages, InputBar, onMessage } = props;
      //const isGuestBlocked = !GUEST_USER_ALLOWED && (!user || !user.role || user.role === 'guest');

      if ([401, 403, 404].includes(statusCode)) {
        return <ShowNotFoundOrUnAuthorizedUiBasedOnStatusCode statusCode={statusCode} />
      }

      return (
        <>
          <Content>
            <div className="property-card-container">
              {isEmpty && <Title />}
              <PropertyCard propertyDetails={propertyDetails} />
            </div>
            <Messages />
          </Content>
          {isContentNotVisible || blockedByGuestAccess ? null : (
            <div className="start_content pills-container">
              <h5 className="suggestions-heading">Suggestions</h5>
              <div className="start_pills m-0 p-0" style={{ justifyContent: "flex-start" }}>
                {questions.map((question, index) => (
                  <div
                    key={index}
                    onClick={() => onMessage(question)}
                    className="filterpill filterpill--transparent h-auto text-decoration-none pr-4 m-1 d-flex cursor--pointer font-weight-normal">
                    <span>{question}</span>
                  </div>
                ))}
              </div>
              <InputBar />
            </div>
          )}
          {user?.role && blockedByGuestAccess &&
            <div className="cpr_content_inner" style={{ width: '100%' }}>
              <SidebarSignUp user={user} assistant={assistant} />
            </div>
          }
        </>
      );
    },
    [assistant, isContentNotVisible, propertyDetails, statusCode, user]
  );

  const pageTitle = isListingDetail ? null : `Ask anything about ${propertyName}`;

  if (!propertyDetails) {
    return (
      <div className="text-center border_radius--pudgy bg_color_cement_light p-4 ml-auto mr-auto" style={{ maxWidth: '450px' }}>
        <div className="d-flex align-items-center">
          <div className="spinner-border mr-4 color_har_blue_dark" style={{ width: '2rem', height: '2rem' }} role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <div className="font_size--medium color_auxiliary">Loading Property Details...</div>
        </div>
      </div>
    )
  }
  return (
    <ErrorBoundary
      fallback={(error: Error) => (
        <ChatErrorFallback
          error={error}
          title="Property Chat Error"
          message="We encountered an issue while loading the property chat. Please try refreshing the page or visiting the property listing directly."
        />
      )}
    >
      <MetaHead />
      <ChatWindow
        title={pageTitle}
        user={user}
        assistant={assistant}
        assistantParams={{ harid }}
        Template={Template}
        onNewAssistantSession={onNewAssistantSession}
        isSetMinHeight={false}
        key={chatKey}
      />
    </ErrorBoundary>
  );
}
